import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import locationService from '../../services/locationService';
import EnrollmentModal from '../common/EnrollmentModal';

// Custom blue marker icon
const blueIcon = new L.Icon({
  iconUrl: '/img/blue_mapcon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [map, setMap] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [enrollmentModal, setEnrollmentModal] = useState({ open: false, location: null });

  useEffect(() => {
    requestUserLocation();
  }, []);

  useEffect(() => {
    if (mapCenter && !map) {
      // Only fetch locations on initial load
      fetchLocations();
    }
  }, [mapCenter, map]);

  const requestUserLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const userLoc = { lat: latitude, lng: longitude };
          setUserLocation(userLoc);
          if (map) {
            updateMapCenterProgrammatically(userLoc);
          } else {
            setMapCenter(userLoc);
          }
          setLocationError(null);
        },
        async (error) => {
          console.error('Error getting user location:', error);
          setLocationError('Location access denied');
          // Fallback to IP-based geolocation
          await getLocationFromIP();
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      setLocationError('Geolocation not supported');
      getLocationFromIP();
    }
  };

  const getLocationFromIP = async () => {
    try {
      // Using a free IP geolocation service as fallback
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      console.log(data);
      if (data.latitude && data.longitude) {
        const ipLoc = { lat: data.latitude, lng: data.longitude };
        setMapCenter(ipLoc);
        setLocationError(null);
      }
    } catch (error) {
      console.error('Error getting location from IP:', error);
      setMapCenter({ lat: 37.7749, lng: -122.4194 }); // Default to San Francisco
    }
  };

  const fetchLocations = async (e = undefined) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      const response = await locationService.getNearbyLocations(mapCenter.lat, mapCenter.lng, searchQuery);
      setLocations(response.locations);
      setLoading(false);
      setInitialLoading(false);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const updateMapCenterProgrammatically = (newCenter) => {
    setMapCenter(newCenter);
    setShowRefreshButton(false);
    if (map) {
      map.flyTo([newCenter.lat, newCenter.lng], map.getZoom(), {
        animate: true,
        duration: 1.5
      });
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    // Update map center to selected location
    updateMapCenterProgrammatically({ lat: location.coordinates.lat, lng: location.coordinates.lon });
  };

  const handleCenterOnUser = () => {
    if (userLocation) {
      updateMapCenterProgrammatically(userLocation);
    } else {
      requestUserLocation();
    }
  };

  const handleRefreshLocations = () => {
    fetchLocations();
    setShowRefreshButton(false);
  };

  const handleEnrollClick = (location) => {
    setEnrollmentModal({ open: true, location });
  };

  const handleEnrollSuccess = (enrolledLocation) => {
    // Update the location to show as enrolled
    setLocations(prevLocations =>
      prevLocations.map(loc =>
        loc.group_id === enrolledLocation.group_id
          ? { ...loc, member_info: { enrolled: true } }
          : loc
      )
    );
  };

  // Calculate distance between two coordinates in miles
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Radius of the Earth in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(1);
  };

  // Component to handle map events
  const MapEventHandler = () => {
    const mapInstance = useMap();

    useEffect(() => {
      const handleMoveEnd = () => {
        const center = mapInstance.getCenter();
        setMapCenter({ lat: center.lat, lng: center.lng });
        setShowRefreshButton(true);
      };

      mapInstance.on('moveend', handleMoveEnd);

      return () => {
        mapInstance.off('moveend', handleMoveEnd);
      };
    }, [mapInstance]);

    return null;
  };

  const renderLocationsList = () => {
    if (initialLoading || (loading && locations.length === 0)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (locations.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body1">No locations found. Try a different search.</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {locations.map((location, index) => (
          <React.Fragment key={location.location_id}>
            <ListItem
              alignItems="flex-start"
              sx={{
                py: 2,
                cursor: 'pointer',
                bgcolor: selectedLocation?.location_id === location.location_id ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
              }}
              onClick={() => handleLocationSelect(location)}
            >
              {location.logo && (
                <ListItemAvatar>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      variant="rounded"
                      src={location.logo}
                      sx={{ width: 48, height: 48, mr: 1 }}
                    >
                      <LocationOnIcon />
                    </Avatar>
                    {location.member_info && Object.keys(location.member_info).length > 0 && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          bottom: -4,
                          right: 4,
                          fontSize: 20,
                          color: 'success.main',
                          backgroundColor: 'white',
                          borderRadius: '50%'
                        }}
                      />
                    )}
                  </Box>
                </ListItemAvatar>
              )}
              <ListItemText
                primary={
                  <Typography variant="subtitle1" component="div" fontWeight="bold">
                    {location.location_name}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {location.address.address1}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {location.address.city}, {location.address.state} {location.address.zip_code}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {mapCenter && calculateDistance(mapCenter.lat, mapCenter.lng, location.coordinates.lat, location.coordinates.lon)} miles away
                    </Typography>
                    {(!location.member_info || Object.keys(location.member_info).length === 0) && (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnrollClick(location);
                        }}
                      >
                        Sign up now
                      </Button>
                    )}
                  </>
                }
              />
            </ListItem>
            {index < locations.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderMap = () => {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          borderRadius: 1,
          '.leaflet-container': {
            height: '100%',
            width: '100%',
            borderRadius: 1,
          }
        }}
      >
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          whenReady={(event) => setMap(event.target)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Map move handler */}
          <MapEventHandler />

          {/* Location markers */}
          {locations.map(location => (
            <Marker
              key={location.location_id}
              position={[location.coordinates.lat, location.coordinates.lon]}
              icon={blueIcon}
              eventHandlers={{
                click: () => handleLocationSelect(location)
              }}
            />
          ))}
        </MapContainer>
      </Box>
    );
  };

  const renderLocationDetails = () => {
    if (!selectedLocation) return null;

    return (
      <Card sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: '350px',
        maxWidth: 'calc(100% - 32px)',
        zIndex: 1000,
        opacity: 0.98,
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(3px)'
      }}>
        <CardContent sx={{ position: 'relative' }}>
          <IconButton
            size="small"
            sx={{ position: 'absolute', top: 0, right: 0 }}
            onClick={() => setSelectedLocation(null)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box>
            {/* Header with logo and name */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {selectedLocation.logo && (
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    variant="rounded"
                    src={selectedLocation.logo}
                    sx={{ width: 56, height: 56 }}
                  >
                    <LocationOnIcon />
                  </Avatar>
                  {selectedLocation.member_info && Object.keys(selectedLocation.member_info).length > 0 && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        fontSize: 24,
                        color: 'success.main',
                        backgroundColor: 'white',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                </Box>
              )}
              <Box sx={{ flex: 1, alignSelf: 'center' }}>
                <Typography variant="h6">
                  {selectedLocation.location_name || selectedLocation.name}
                </Typography>
                {selectedLocation.member_info && Object.keys(selectedLocation.member_info).length > 0 && (
                  <Typography variant="body2" color="success.main">
                    Enrolled
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Details with icons aligned to content */}
            <Box sx={{ pl: '25px' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, ml: -3 }}>
                <LocationOnIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2">
                  {selectedLocation.address.address1}<br />
                  {selectedLocation.address.city}, {selectedLocation.address.state} {selectedLocation.address.zip_code}
                </Typography>
              </Box>

              {selectedLocation.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: -3 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2">{selectedLocation.phone}</Typography>
                </Box>
              )}

              {selectedLocation.store_hours && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: -3 }}>
                  <AccessTimeIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2">{selectedLocation.store_hours}</Typography>
                </Box>
              )}

              {(!selectedLocation.member_info || Object.keys(selectedLocation.member_info).length === 0) && (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    ml: -3,
                    textTransform: 'none'
                  }}
                  onClick={() => {
                    handleEnrollClick(selectedLocation);
                  }}
                >
                  Sign up now
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ height: 'calc(100vh - 155px)', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Discover Locations
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box component="form" onSubmit={fetchLocations} sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              placeholder="Search by address or location name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<MyLocationIcon />}
            onClick={handleCenterOnUser}
            sx={{
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': {
                margin: { xs: 0, sm: '0 8px 0 -4px' }
              }
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
              My Location
            </Box>
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {/* Locations List */}
          <Grid item xs={12} md={4} sx={{ height: '100%', display: { xs: 'none', md: 'block' } }}>
            <Paper
              elevation={2}
              sx={{
                width: '300px',
                height: '100%',
                overflow: 'auto'
              }}
            >
              {renderLocationsList()}
            </Paper>
          </Grid>

          {/* Map Area */}
          <Grid item xs={12} md={8} sx={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                {mapCenter && renderMap()}
                {selectedLocation && renderLocationDetails()}
                {showRefreshButton && (
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshLocations}
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    Search this area
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Enrollment Modal */}
      <EnrollmentModal
        open={enrollmentModal.open}
        onClose={() => setEnrollmentModal({ open: false, location: null })}
        location={enrollmentModal.location}
        onSuccess={handleEnrollSuccess}
      />
    </Container>
  );
};

export default LocationsPage;